// ── Backend quote exports — CSV (spreadsheet) and printable HTML (PDF) ──────
//
// These are the INTERNAL backend: our costs, markups, PST, labour hours and
// margin. They are not the client-facing quote — Review & Push is that — and
// both outputs say so on their face, because a backend sheet emailed to a
// client hands them our cost structure.
//
// Pure and dependency-free so scripts/test_quote_export.js can replay real
// quote_json blobs through it without mounting the editor. The PDF is produced
// by the same printable-popup route the load calculator already uses
// (LoadCalcWorkspace): build self-contained HTML, open it, call print. No new
// dependency, and the browser's own "Save as PDF" does the conversion.
//
// 🚨 The money math here MUST stay identical to recomputeTotals() in
// QuoteDraftReview.jsx:
//     cost_with_pst = round2(our_cost × (1 + pst/100))
//     client_each   = round2(cost_with_pst × (1 + markup/100))
//     client_total  = round2(client_each × qty)
// PST is baked into COST (Browning convention), never shown as a client line.
// An export that rounds differently is worse than no export: it looks
// authoritative and disagrees with the screen.

export const LABOUR_COST_PER_VH = 80;

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const num = (n) => (Number.isFinite(Number(n)) ? Number(n) : 0);

export const money = (n) =>
  (num(n) < 0 ? "-$" : "$") + Math.abs(num(n)).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** One material line, priced exactly as the editor prices it. */
export function priceLine(m) {
  const qty = num(m.qty ?? 1);
  const our_cost = num(m.our_cost);
  const pst_pct = num(m.pst_pct);
  const markup_pct = num(m.markup_pct);
  const cost_with_pst = round2(our_cost * (1 + pst_pct / 100));
  const client_each = round2(cost_with_pst * (1 + markup_pct / 100));
  return {
    qty,
    unit: m.unit || "ea",
    description: String(m.description || ""),
    category: String(m.material_category || m.section || ""),
    purpose: String(m.purpose || ""),
    our_cost,
    pst_pct,
    markup_pct,
    cost_with_pst,
    extended_cost: round2(cost_with_pst * qty),
    client_each,
    client_total: round2(client_each * qty),
    price_source: m._price_source?.tier || (m._tech_edit ? "tech_set" : ""),
    hand_priced: !!m._tech_edit,
  };
}

/** Every option (or phase, or the single option) of a saved quote_json. */
export function exportOptions(quoteJson) {
  const j = quoteJson || {};
  if (Array.isArray(j.options) && j.options.length) {
    return j.options.map((o, i) => ({ label: o.label || `Option ${i + 1}`, recommended: !!o.recommended, kind: "option", draft: o.draft || {} }));
  }
  if (Array.isArray(j.sections) && j.sections.length) {
    return j.sections.map((s, i) => ({ label: s.label || `Phase ${i + 1}`, recommended: false, kind: "phase", draft: s.draft || {} }));
  }
  return [{ label: j.meta?.tier_label || "Quote", recommended: true, kind: "option", draft: j }];
}

/** Totals for one option, recomputed from its lines (never trusted blindly). */
export function optionTotals(draft) {
  const d = draft || {};
  const lines = (d.materials || []).map(priceLine);
  const materials_sell = round2(lines.reduce((t, l) => t + l.client_total, 0));
  const materials_cost = round2(lines.reduce((t, l) => t + l.extended_cost, 0));

  const hours = num(d.labour?.total_hours);
  const rate = num(d.labour?.rate);
  const labour_sell = round2(d.labour?.total ?? hours * rate);
  const labour_cost = round2(hours * LABOUR_COST_PER_VH);

  const permit_fee = num(d.totals?.permit_fee ?? d.permit_fee);
  const permit_markup_pct = num(d.totals?.permit_markup_pct ?? d.permit_markup_pct ?? 35);
  const permit_sell = round2(permit_fee * (1 + permit_markup_pct / 100));

  const sell = round2(materials_sell + labour_sell + permit_sell);
  const cost = round2(materials_cost + labour_cost + permit_fee);
  return {
    lines, hours, rate,
    materials_sell, materials_cost,
    labour_sell, labour_cost,
    permit_fee, permit_sell,
    sell, cost,
    margin: round2(sell - cost),
    margin_pct: sell > 0 ? round2(((sell - cost) / sell) * 100) : 0,
    // What the app itself saved, so a mismatch is visible rather than papered
    // over — if these disagree the quote needs re-saving, and silently printing
    // our own recomputation would hide that.
    saved_grand_total: d.totals?.grand_total ?? null,
  };
}

// ── CSV ──────────────────────────────────────────────────────────────────────

// Descriptions carry commas, quotes and inch marks (`2" PVC Conduit, grey`).
// RFC-4180: wrap anything with a comma, quote, or newline, and double inner
// quotes. A naive join(",") shifts every column after the first comma.
const csvCell = (v) => {
  const s = v == null ? "" : String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const csvRow = (cells) => cells.map(csvCell).join(",");

const CSV_HEADER = [
  "Option", "Recommended", "Section", "Qty", "Unit", "Description", "Purpose",
  "Our Cost (ex PST)", "PST %", "Cost w/ PST", "Extended Cost",
  "Markup %", "Sell Each", "Sell Total", "Price Source", "Hand Priced",
];

/**
 * The whole backend quote as one flat CSV: every line of every option, then a
 * per-option totals block. Flat on purpose — it pivots.
 */
export function buildBackendCsv(quoteJson, meta = {}) {
  const opts = exportOptions(quoteJson);
  const rows = [];

  rows.push(csvRow(["ElecBid backend quote — INTERNAL (our costs and margin). Not for the client."]));
  rows.push(csvRow([`Client:`, meta.client || "", `Quote #:`, meta.quote_number || "", `Title:`, meta.title || ""]));
  rows.push(csvRow([`Exported:`, new Date().toISOString().slice(0, 16).replace("T", " ")]));
  rows.push("");
  rows.push(csvRow(CSV_HEADER));

  for (const o of opts) {
    const t = optionTotals(o.draft);
    for (const l of t.lines) {
      rows.push(csvRow([
        o.label, o.recommended ? "YES" : "", l.category || "Material",
        l.qty, l.unit, l.description, l.purpose,
        l.our_cost, l.pst_pct, l.cost_with_pst, l.extended_cost,
        l.markup_pct, l.client_each, l.client_total,
        l.price_source, l.hand_priced ? "YES" : "",
      ]));
    }
    if (t.hours > 0 || t.labour_sell > 0) {
      rows.push(csvRow([o.label, o.recommended ? "YES" : "", "Labour",
        t.hours, "h", `Labour @ ${money(t.rate)}/h`, "",
        LABOUR_COST_PER_VH, 0, LABOUR_COST_PER_VH, t.labour_cost,
        "", t.rate, t.labour_sell, "van_rate", ""]));
    }
    if (t.permit_fee > 0) {
      rows.push(csvRow([o.label, o.recommended ? "YES" : "", "Permit",
        1, "ea", "Electrical permit (TSBC schedule)", "",
        t.permit_fee, 0, t.permit_fee, t.permit_fee,
        "", t.permit_sell, t.permit_sell, "tsbc_schedule", ""]));
    }
  }

  rows.push("");
  rows.push(csvRow(["TOTALS BY OPTION"]));
  rows.push(csvRow(["Option", "Recommended", "Materials Sell", "Labour Hours", "Labour Sell", "Permit Sell", "TOTAL SELL", "Total Cost", "Margin $", "Margin %", "Saved Grand Total"]));
  for (const o of opts) {
    const t = optionTotals(o.draft);
    rows.push(csvRow([
      o.label, o.recommended ? "YES" : "",
      t.materials_sell, t.hours, t.labour_sell, t.permit_sell,
      t.sell, t.cost, t.margin, t.margin_pct,
      t.saved_grand_total == null ? "" : t.saved_grand_total,
    ]));
  }
  // A sectioned quote's phases ADD UP; pick-one options do not, so summing them
  // would be meaningless and is deliberately omitted.
  if (opts.length > 1 && opts[0].kind === "phase") {
    const sum = opts.reduce((t, o) => t + optionTotals(o.draft).sell, 0);
    rows.push(csvRow(["ALL PHASES (these ADD UP)", "", "", "", "", "", round2(sum)]));
  }

  return rows.join("\r\n");
}

// ── Printable HTML (→ PDF via the browser) ───────────────────────────────────

const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * A self-contained page. No external CSS, fonts or images — it is written into
 * a popup with document.write and printed, so it must stand alone.
 */
export function buildBackendHtml(quoteJson, meta = {}) {
  const opts = exportOptions(quoteJson);
  const when = new Date().toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" });

  const optionBlock = (o) => {
    const d = o.draft || {};
    const t = optionTotals(d);
    const mismatch = t.saved_grand_total != null && Math.abs(t.saved_grand_total - t.sell) > 0.5;
    return `
  <section class="opt">
    <h2>${esc(o.label)}${o.recommended ? ' <span class="rec">RECOMMENDED</span>' : ""}${o.kind === "phase" ? ' <span class="rec phase">PHASE — these add up</span>' : ""}</h2>
    <div class="sum">
      <div><span>Materials</span><b>${money(t.materials_sell)}</b></div>
      <div><span>Labour · ${t.hours}h @ ${money(t.rate)}</span><b>${money(t.labour_sell)}</b></div>
      ${t.permit_fee > 0 ? `<div><span>Permit</span><b>${money(t.permit_sell)}</b></div>` : ""}
      <div class="tot"><span>TOTAL</span><b>${money(t.sell)}</b></div>
      <div class="int"><span>Cost</span><b>${money(t.cost)}</b></div>
      <div class="int"><span>Margin</span><b>${money(t.margin)} · ${t.margin_pct}%</b></div>
    </div>
    ${mismatch ? `<p class="warn">⚠ Recomputed total ${money(t.sell)} differs from the saved grand total ${money(t.saved_grand_total)} — re-save the quote before relying on this sheet.</p>` : ""}
    ${t.lines.length ? `
    <table>
      <thead><tr>
        <th class="r">Qty</th><th>Unit</th><th>Description</th>
        <th class="r">Our cost</th><th class="r">PST</th><th class="r">Ext. cost</th>
        <th class="r">Mk</th><th class="r">Sell ea</th><th class="r">Sell total</th>
      </tr></thead>
      <tbody>
        ${t.lines.map((l, i) => `${l.category && l.category !== t.lines[i - 1]?.category ? `<tr><th colspan="9">${esc(l.category)}</th></tr>` : ""}<tr>
          <td class="r">${esc(l.qty)}</td><td>${esc(l.unit)}</td>
          <td>${esc(l.description)}${l.purpose ? `<br><i>${esc(l.purpose)}</i>` : ""}${l.hand_priced ? ' <span class="hp">hand&nbsp;priced</span>' : ""}</td>
          <td class="r">${money(l.our_cost)}</td><td class="r">${l.pst_pct}%</td><td class="r">${money(l.extended_cost)}</td>
          <td class="r">${l.markup_pct}%</td><td class="r">${money(l.client_each)}</td><td class="r">${money(l.client_total)}</td>
        </tr>`).join("")}
      </tbody>
    </table>` : `<p class="empty">No material lines.</p>`}
    ${(d.scope || []).length ? `<h3>Scope of Work</h3><ul>${d.scope.map((x) => x.is_heading ? `<li style="list-style:none;font-weight:700;margin-top:9px">${esc(x.text)}</li>` : `<li>${esc(x.text)}</li>`).join("")}</ul>` : ""}
    ${(d.notes || []).length ? `<h3>Notes</h3><ul>${d.notes.map((x) => `<li>${esc(x.text)}</li>`).join("")}</ul>` : ""}
    ${(d.exclusions || []).length ? `<h3>Not Included</h3><ul>${d.exclusions.map((x) => `<li>${esc(x.text)}</li>`).join("")}</ul>` : ""}
  </section>`;
  };

  return `<!doctype html><html><head><meta charset="utf-8">
<title>${esc(meta.title || "Backend quote")}</title>
<style>
  * { box-sizing: border-box; }
  body { font: 12px/1.45 -apple-system, "Segoe UI", system-ui, sans-serif; color: #111827; margin: 0; padding: 26px 30px; }
  h1 { font-size: 17px; margin: 0 0 2px; }
  .meta { color: #6b7280; font-size: 11.5px; margin-bottom: 4px; }
  .internal { display: inline-block; background: #fef3c7; border: 1px solid #f59e0b; color: #92400e;
              font-weight: 700; font-size: 10.5px; letter-spacing: .05em; padding: 3px 9px; border-radius: 4px; margin: 8px 0 16px; }
  .opt { margin: 0 0 26px; page-break-inside: avoid; }
  h2 { font-size: 14px; margin: 18px 0 8px; padding-bottom: 5px; border-bottom: 2px solid #111827; }
  .rec { background: #111827; color: #fff; font-size: 9.5px; padding: 2px 7px; border-radius: 3px; vertical-align: middle; letter-spacing: .05em; }
  .rec.phase { background: #2563eb; }
  .sum { display: flex; flex-wrap: wrap; gap: 6px 22px; margin-bottom: 10px; font-size: 12px; }
  .sum div { display: flex; gap: 7px; }
  .sum span { color: #6b7280; }
  .sum .tot b { font-size: 14px; }
  .sum .int span, .sum .int b { color: #b45309; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 10px; }
  th, td { padding: 4px 6px; border-bottom: 1px solid #e5e7eb; text-align: left; vertical-align: top; }
  th { background: #f9fafb; font-size: 10.5px; text-transform: uppercase; letter-spacing: .04em; color: #6b7280; border-bottom: 1px solid #d1d5db; }
  td.r, th.r { text-align: right; white-space: nowrap; }
  td i { color: #6b7280; font-size: 11px; }
  .hp { background: #dbeafe; color: #1e40af; font-size: 9.5px; padding: 1px 5px; border-radius: 3px; }
  h3 { font-size: 11.5px; text-transform: uppercase; letter-spacing: .05em; color: #6b7280; margin: 12px 0 4px; }
  ul { margin: 0 0 8px; padding-left: 18px; }
  li { margin-bottom: 2px; }
  .warn { background: #fef2f2; border: 1px solid #fca5a5; color: #991b1b; padding: 6px 9px; border-radius: 4px; }
  .empty { color: #9ca3af; font-style: italic; }
  .print-bar { position: fixed; top: 8px; right: 10px; }
  .print-bar button { padding: 7px 13px; border: 1px solid #d1d5db; border-radius: 6px; background: #fff; cursor: pointer; font-size: 12.5px; }
  .print-bar button.primary { background: #111827; color: #fff; border-color: #111827; font-weight: 600; }
  @media print { body { padding: 0; } .no-print { display: none; } @page { margin: 12mm; } }
</style></head><body>
  <div class="print-bar no-print">
    <button class="primary" onclick="window.print()">Save as PDF / Print</button>
    <button onclick="window.close()">Close</button>
  </div>
  <h1>${esc(meta.title || "Backend quote")}</h1>
  <div class="meta">${esc(meta.client || "")}${meta.quote_number ? ` · Quote #${esc(meta.quote_number)}` : ""}${meta.address ? ` · ${esc(meta.address)}` : ""}</div>
  <div class="meta">Exported ${esc(when)}</div>
  <div class="internal">INTERNAL — our costs, markups and margin. Not the client quote.</div>
  ${opts.map(optionBlock).join("")}
  <script>window.addEventListener('load', function () { setTimeout(function () { window.print(); }, 350); });<\/script>
</body></html>`;
}

/** Safe, descriptive filename stem: "2666-terry-deans-backend". */
export function exportFilename(meta = {}, ext = "csv") {
  const ident = [meta.quote_number, meta.client || meta.title]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  // With nothing to identify it, "backend-quote" beats a bare "backend" —
  // several of these end up in one Downloads folder.
  return `${ident ? `${ident}-backend` : "backend-quote"}.${ext}`;
}
